import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { Skeleton } from '@/common/ui/skeleton'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
} from '@/common/ui/form'

import { ChatMessageType } from '@/schema'
import {
	useQueryChatSettings,
	useMutationUpdateChatSettings,
} from '../../../api/hooks'
import { greetingSettingsSchema } from '../types'
import type { GreetingSettingsFormData, MessageContentInput } from '../types'
import { MessageListEditor } from './MessageListEditor'
import { MessageListPreview } from './MessagePreview'

export function GreetingSettingsPage() {
	const { data: settings, isLoading } = useQueryChatSettings()
	const updateMutation = useMutationUpdateChatSettings()
	const [showPreview, setShowPreview] = useState(false)

	const form = useForm<GreetingSettingsFormData>({
		resolver: zodResolver(greetingSettingsSchema),
		defaultValues: {
			greetingEnabled: false,
			greetingMessages: [],
		},
		values: settings
			? {
					greetingEnabled: settings.greetingEnabled,
					greetingMessages: transformResponseToInput(settings.greetingMessages),
				}
			: undefined,
	})

	const greetingEnabled = form.watch('greetingEnabled')
	const greetingMessages = form.watch('greetingMessages')

	const handleSubmit = (data: GreetingSettingsFormData) => {
		updateMutation.mutate({
			greetingEnabled: data.greetingEnabled,
			greetingMessages: data.greetingMessages.map((msg, index) => ({
				messageType: msg.messageType,
				order: index,
				...(msg.text && { text: msg.text }),
				...(msg.stickerPackageId && { stickerPackageId: msg.stickerPackageId }),
				...(msg.stickerId && { stickerId: msg.stickerId }),
				...(msg.imageUrl && { imageUrl: msg.imageUrl }),
				...(msg.videoUrl && { videoUrl: msg.videoUrl }),
				...(msg.videoPreviewUrl && { videoPreviewUrl: msg.videoPreviewUrl }),
				...(msg.audioUrl && { audioUrl: msg.audioUrl }),
				...(msg.audioDuration !== undefined && {
					audioDuration: msg.audioDuration,
				}),
				...(msg.fileUrl && { fileUrl: msg.fileUrl }),
				...(msg.fileName && { fileName: msg.fileName }),
				...(msg.locationTitle && { locationTitle: msg.locationTitle }),
				...(msg.locationAddress && { locationAddress: msg.locationAddress }),
				...(msg.locationLatitude !== undefined && {
					locationLatitude: msg.locationLatitude,
				}),
				...(msg.locationLongitude !== undefined && {
					locationLongitude: msg.locationLongitude,
				}),
				...(msg.flexAltText && { flexAltText: msg.flexAltText }),
				...(msg.flexContents && {
					flexContents: msg.flexContents as Record<string, never>,
				}),
			})),
		})
	}

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>ข้อความต้อนรับ</CardTitle>
								<CardDescription>
									ส่งข้อความต้อนรับอัตโนมัติเมื่อลูกค้าเริ่มแชทครั้งแรก
									(รองรับหลายข้อความ)
								</CardDescription>
							</div>
							{greetingEnabled && greetingMessages.length > 0 && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => setShowPreview(!showPreview)}
								>
									{showPreview ? (
										<>
											<EyeOff className="mr-2 h-4 w-4" />
											ซ่อนตัวอย่าง
										</>
									) : (
										<>
											<Eye className="mr-2 h-4 w-4" />
											ดูตัวอย่าง
										</>
									)}
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<FormField
							control={form.control}
							name="greetingEnabled"
							render={({ field }) => (
								<FormItem className="flex items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											เปิดใช้งานข้อความต้อนรับ
										</FormLabel>
										<FormDescription>
											ส่งข้อความอัตโนมัติเมื่อลูกค้าเริ่มแชทใหม่
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						{greetingEnabled && (
							<div className="grid gap-6 lg:grid-cols-2">
								<div className="space-y-4">
									<h4 className="text-sm font-medium">ข้อความต้อนรับ</h4>
									<FormField
										control={form.control}
										name="greetingMessages"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<MessageListEditor
														messages={field.value}
														onChange={field.onChange}
														maxMessages={5}
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								{showPreview && (
									<div className="space-y-4">
										<h4 className="text-sm font-medium">ตัวอย่างข้อความ</h4>
										<MessageListPreview messages={greetingMessages} />
									</div>
								)}
							</div>
						)}
					</CardContent>
				</Card>

				<div className="flex justify-end">
					<Button type="submit" disabled={updateMutation.isPending}>
						<Save className="mr-2 h-4 w-4" />
						บันทึกการตั้งค่า
					</Button>
				</div>
			</form>
		</Form>
	)
}

// ==================== Helpers ====================

function transformResponseToInput(
	messages: Array<{
		id: string
		order: number
		messageType: string
		text?: string
		stickerPackageId?: string
		stickerId?: string
		imageUrl?: string
		videoUrl?: string
		videoPreviewUrl?: string
		audioUrl?: string
		audioDuration?: number
		fileUrl?: string
		fileName?: string
		locationTitle?: string
		locationAddress?: string
		locationLatitude?: number
		locationLongitude?: number
		flexAltText?: string
		flexContents?: Record<string, unknown>
	}>,
): MessageContentInput[] {
	return messages
		.sort((a, b) => a.order - b.order)
		.map((msg) => ({
			id: msg.id,
			order: msg.order,
			messageType: msg.messageType as ChatMessageType,
			text: msg.text,
			stickerPackageId: msg.stickerPackageId,
			stickerId: msg.stickerId,
			imageUrl: msg.imageUrl,
			videoUrl: msg.videoUrl,
			videoPreviewUrl: msg.videoPreviewUrl,
			audioUrl: msg.audioUrl,
			audioDuration: msg.audioDuration,
			fileUrl: msg.fileUrl,
			fileName: msg.fileName,
			locationTitle: msg.locationTitle,
			locationAddress: msg.locationAddress,
			locationLatitude: msg.locationLatitude,
			locationLongitude: msg.locationLongitude,
			flexAltText: msg.flexAltText,
			flexContents: msg.flexContents,
		}))
}
